import { ActivityIndicator, StyleSheet } from 'react-native';
import { FadeIn } from '../../../components/animations';
import { Box, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { motion } from '../../../theme/motion';
import type { CalendarEvent, CalendarView } from '../../../types/calendar';
import type { BusinessHours } from '../../settings/utils/businessHours';
import { getVisibleDayWindow } from '../utils/calendarBusinessWindow';
import { getViewerTimeZone, getWeekRange, type LocalDate } from '../utils/calendarDate';
import { CalendarAgendaList } from './CalendarAgendaList';
import { CalendarDayTimeline } from './CalendarDayTimeline';
import { CalendarMonthGrid } from './CalendarMonthGrid';
import { CalendarWeekGrid } from './CalendarWeekGrid';
import { EventPreviewLayer } from './EventPreviewLayer';

interface CalendarScreenProps {
  view: CalendarView;
  /** The period currently in view — see calendarViewRange.ts's getRangeKey/
   * stepAnchor for how the route computes this from `view`. */
  anchor: LocalDate;
  events: CalendarEvent[] | undefined;
  isLoading: boolean;
  error: Error | null;
  /** Defaults to the viewer's own zone; overridable for tests. */
  timeZone?: string;
  /** The org's configured working hours; trims the Day/Week grid to them.
   * Absent (or not enabled) renders the full 24 hours, as before. */
  businessHours?: BusinessHours | null;
  weekStartsOn?: 0 | 1;
  onEventPress?: (event: CalendarEvent) => void;
  /** Desktop drag-to-reschedule; see useEventDrag for why mobile has none. */
  onReschedule?: (event: CalendarEvent, span: { startsAt: string; endsAt: string }) => void;
  /** Month grid's day-cell tap — drills into Day view anchored on that date. */
  onSelectDay?: (date: LocalDate) => void;
}

// CalendarMonthGrid always calls onSelectDay; this only stands in when the
// route hasn't wired one up (e.g. a test rendering CalendarScreen in
// isolation), so a tap on a day cell is a safe no-op instead of a crash.
function noopSelectDay() {
  // intentionally empty — see comment above
}

// Mirrors KanbanBoard.tsx's loading/error branch structure exactly — same
// order, same shape. Unlike the pre-Unit-3 day-list this replaced, an empty
// Month/Week/Day still IS the calendar (an empty grid, not an empty page) —
// Google Calendar's views never disappear just because a period has nothing
// in it, so there is no "no upcoming meetings" branch left below.
export function CalendarScreen({
  view,
  anchor,
  events,
  isLoading,
  error,
  timeZone = getViewerTimeZone(),
  businessHours,
  weekStartsOn = 0,
  onEventPress,
  onReschedule,
  onSelectDay,
}: Readonly<CalendarScreenProps>) {
  if (isLoading) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center" padding="xxl">
        <ActivityIndicator size="large" color={colors.primary[500]} />
        <Text variant="body" color={colors.text.tertiary} style={{ marginTop: 12 }}>
          Loading calendar…
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center" padding="xxl">
        <Text variant="body" color={colors.status.error.main}>
          Could not load the calendar.
        </Text>
        <Text variant="caption" color={colors.text.tertiary} style={{ marginTop: 4 }}>
          {error.message}
        </Text>
      </Box>
    );
  }

  // One preview layer around whichever view is showing: hover/tap previews
  // float above the whole grid instead of inside a clipping cell or block.
  return (
    <EventPreviewLayer>
      {/* Keyed on the view so a switch remounts and replays the entrance.
          Month used to stagger its week rows while Week, Day and Agenda
          appeared instantly, so switching between them looked broken rather
          than deliberate. One entrance at this level makes all four agree. */}
      <FadeIn key={view} duration={motion.durations.normal} translateY={8} style={styles.view}>
        {renderView()}
      </FadeIn>
    </EventPreviewLayer>
  );

  function renderView() {
    if (view === 'month') {
      return (
        <CalendarMonthGrid
          anchor={anchor}
          events={events ?? []}
          timeZone={timeZone}
          onSelectDay={onSelectDay ?? noopSelectDay}
        />
      );
    }

    if (view === 'agenda') {
      return (
        <CalendarAgendaList
          anchor={anchor}
          events={events ?? []}
          timeZone={timeZone}
          onEventPress={onEventPress}
        />
      );
    }

    if (view === 'day') {
      return (
        <CalendarDayTimeline
          date={anchor}
          events={events ?? []}
          timeZone={timeZone}
          window={getVisibleDayWindow({
            businessHours,
            days: [anchor],
            events: events ?? [],
            timeZone,
          })}
          onEventPress={onEventPress}
          onReschedule={onReschedule}
        />
      );
    }

    // view === 'week' — the only case left. This branch used to be an
    // unguarded fallthrough, so widening CalendarView silently rendered the
    // new view as Week; the switches in calendarViewRange.ts now fail to
    // compile instead, and this one is explicit.
    return (
      <CalendarWeekGrid
        anchor={anchor}
        events={events ?? []}
        timeZone={timeZone}
        weekStartsOn={weekStartsOn}
        window={getVisibleDayWindow({
          businessHours,
          days: getWeekRange(anchor, weekStartsOn),
          events: events ?? [],
          timeZone,
        })}
        onEventPress={onEventPress}
        onReschedule={onReschedule}
      />
    );
  }
}

const styles = StyleSheet.create({
  view: {
    flex: 1,
  },
});
